import moment from 'moment';

import {FeatureGate, INTERNAL_PROJECTS, ResourceType, SPECIAL_EVENTS, UserFeatureFlag} from 'common/report/constants';
import {
  Cohort as CohortType,
  EventDefinition,
  MpEvent,
  MpProperty,
  PropertyDefinition,
} from 'common/util/definitions-types';
import {Cohort} from 'common/widgets/cohorts-query-builder/models/cohort';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {getDefinitionForEvent, getDefinitionForProperty} from 'common/util/definitions';
import {abbreviateNumber} from 'common/util/index';
import {getReportUrl} from 'common/report/util';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {EntityTypeNameSingular} from 'common/report/util/saved-entities/types';
import {featuresStore} from 'common/report/stores/features';
import {getShareCapability, isAllowedSuperadminOverride} from 'common/report/util/saved-entities';

import {getCustomEventComposition} from '../custom-event-composition/util';
import {isBehavioralCustomProperty, isPersistedCustomProperty} from '../custom-property-modal/util';

const TYPE_ICONS = {
  string: `type-text`,
  number: `type-number`,
  boolean: `type-boolean`,
  datetime: `type-date`,
  dimension: `table`,
  list: `type-list`,
};

type HeaderParams = {
  name: string;
  displayName?: string;
};

function isInternalProject() {
  return INTERNAL_PROJECTS.has(parseInt(getProjectId(), 10));
}

function getHeaders({name, displayName}: HeaderParams) {
  if (displayName) {
    return {header: displayName, subheader: `Tracked as: ${name}`};
  } else {
    return {header: name, subheader: null};
  }
}

function getSubHeader(cohort: CohortType, isInlineCohort) {
  if (cohort.createdBy) {
    return `Created by ` + cohort.createdBy.name;
  } else if (isInlineCohort) {
    return `Unsaved cohort`;
  } else {
    return ``;
  }
}

function isCustomPropertySaved(prop) {
  return !isNaN(prop.customPropertyId);
}

function getEventLink(event) {
  let pathBlock = ``;
  if (event.customEventId) {
    pathBlock = `#transformations/custom-events/$custom_event:${encodeURIComponent(event.customEventId)}`;
  } else {
    pathBlock = `#transformations/events/${encodeURIComponent(event.name)}`;
  }
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(`lexicon`, {projectId, workspaceId}) + pathBlock;
}

function getPropertyLink(property) {
  let pathBlock = ``;
  const propertyIdentifier = property.customPropertyId
    ? `$custom_property:${property.customPropertyId}`
    : property.name;

  if (property.resourceType === ResourceType.User) {
    pathBlock = `#transformations/user-profile-properties/${encodeURIComponent(propertyIdentifier)}`;
  } else {
    pathBlock = `#transformations/event-properties/${encodeURIComponent(propertyIdentifier)}`;
  }
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(`lexicon`, {projectId, workspaceId}) + pathBlock;
}

function getCohortLink(cohort) {
  const url = Cohort.getExploreHashForUsersInCohort({
    cohortId: cohort.id,
    dataGroupId: cohort.dataGroupId,
    cohortName: cohort.name,
  });
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(`explore`, {projectId, workspaceId}) + `${url}`;
}

function shareEnabled() {
  return featuresStore.value?.getFeatureGate(FeatureGate.Sharing);
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTooltipDataForEvent(
  mpEvent: MpEvent,
  eventDefinitions: EventDefinition[],
  canWriteCustomEvent = false,
  {
    allowEdit = true,
    isLoadingAction = false,
    allowDescriptionEdit = false,
    sampleEventUrl = ``,
    canProvideSampleUrl = true,
  } = {},
): ContextPanelData {
  const def = getDefinitionForEvent(mpEvent, eventDefinitions);
  if (!def) {
    return null;
  }

  const details = [];
  const metrics = [];

  let editItem = null;

  if (allowDescriptionEdit) {
    const type = def.customEventId ? EntityTypeNameSingular.CustomEvent : EntityTypeNameSingular.Event;
    const id = def.customEventId ? def.customEventId : def.id;
    editItem = {id, type, name: def.name};
  }
  let headerLink = ``;
  headerLink = getEventLink(def);

  if (def.isMixpanelDefinition) {
    details.push({icon: `info`, name: `Automatic`, value: `Automatically tracked by Mixpanel`});
  }

  if (def.platforms && def.platforms.length) {
    details.push({icon: `dataset`, name: `Platforms`, value: def.platforms.join(`, `)});
  }

  if (def.tags && def.tags.length) {
    details.push({icon: `tag`, name: `Tags`, value: def.tags.join(`, `)});
  }

  if (def.count) {
    metrics.push({name: `volume (30 days)`, value: abbreviateNumber(def.count, 1, true)});
  }

  if (def.uiQueryCount || def.apiQueryCount) {
    const queryCount = +def.uiQueryCount + +def.apiQueryCount;
    metrics.push({name: `queries (30 days)`, value: abbreviateNumber(queryCount, 1, true)});
  } else if (def.count) {
    // only include for non custom events
    metrics.push({name: `queries (30 days)`, value: 0});
  }

  let compositionDef = null;
  if (def.customEventId) {
    const defToCustomEvent = (def) => ({
      id: def.customEventId || def.collectEverythingEventId,
      name: def.name,
      alternatives: def.alternatives,
    });
    const customEvent = defToCustomEvent(def);
    const customEventMap = eventDefinitions
      .filter((def) => def.customEventId || def.collectEverythingEventId)
      .map((def) => defToCustomEvent(def))
      .reduce((map, customEvent) => Object.assign(map, {[`$custom_event:${customEvent.id}`]: customEvent}), {});
    compositionDef = getCustomEventComposition({customEvent}, !!def.collectEverythingEventId, customEventMap);

    const MAX_LENGTH = 200;
    const reducedParts = [];
    let currLength = 0;
    for (const part of compositionDef.parts) {
      currLength += part.label?.length ?? 0;
      if (currLength >= MAX_LENGTH) {
        reducedParts.push({highlight: false, label: `...`});
        break;
      }
      reducedParts.push(part);
    }
    compositionDef.parts = reducedParts;
  }
  const hasEditPerms = def.can_update_basic || isAllowedSuperadminOverride(def);

  let actions = [];
  if (allowEdit && def.customEventId && canWriteCustomEvent) {
    actions = [
      {
        name: `editCustomEvent`,
        label: `Edit Custom Event`,
        item: {
          id: def.customEventId || def.collectEverythingEventId,
        },
        disabled: !hasEditPerms,
        disabledTooltipText: hasEditPerms ? `` : `You have view-only access and cannot edit`,
      },
    ];
    // Allow context share button for custom events
    if (shareEnabled()) {
      actions.push({
        name: `shareCustomEvent`,
        label: `Share`,
        // Only superadmin or editor can share
        disabled: !getShareCapability(def).enableShare,
        disabledTooltipText: `You have view-only access and cannot share`,
        item: {
          id: def.customEventId || def.collectEverythingEventId,
          superadminOverrideShare: getShareCapability(def).requireSuperadminOverride,
        },
      });
    }
  }

  if (
    !def.customEventId &&
    !Object.values(SPECIAL_EVENTS).includes(def.name) &&
    (isInternalProject() || featuresStore.value?.hasUserFeatureFlag(UserFeatureFlag.Dev))
  ) {
    actions.push({
      name: `viewSampleEvents`,
      label: `View Sample Events`,
      icon: isLoadingAction ? `spinner` : ``,
      disabled: isLoadingAction || !canProvideSampleUrl,
      disabledTooltipText: !canProvideSampleUrl ? `No samples available for this event` : ``,
      item: {
        id: def.id,
        name: def.name,
        url: sampleEventUrl,
      },
    });
  }

  let includeAutotrackDeprecationWarning = false;
  if (def.collectEverythingEventId) {
    details.push({icon: `autotrack`, name: `AutoTrack Event`, value: `Codeless tracking`});
    includeAutotrackDeprecationWarning = true;
  }

  return {
    ...getHeaders(def),
    headerLink,
    description: def.description,
    editItem,
    details,
    metrics,
    compositionDef,
    actions,
    includeAutotrackDeprecationWarning,
  };
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTooltipDataForProperty(
  mpProperty: MpProperty,
  propertyDefinitions: PropertyDefinition[],
  {allowEdit = true, lookupTables = {}, allowDescriptionEdit = false} = {},
): ContextPanelData {
  const def = getDefinitionForProperty(mpProperty, propertyDefinitions);
  if (!def) {
    return null;
  }

  const metrics = [];
  const details = [];

  let editItem = null;

  if (allowDescriptionEdit && !def.customPropertyId) {
    const type =
      def.resourceType === `user` ? EntityTypeNameSingular.UserProperty : EntityTypeNameSingular.EventProperty;
    editItem = {id: def.id, type, name: def.name};
  }
  let headerLink = null;
  if (def.id || (def.customPropertyId && isCustomPropertySaved(def))) {
    headerLink = getPropertyLink(def);
  }

  if (def.type) {
    const displayType =
      def.type === `dimension`
        ? def.customProperty
          ? def.customProperty.joinPropertyType
          : def.join_property_type
        : def.type;
    details.push({icon: TYPE_ICONS[def.type] || TYPE_ICONS.string, name: `Type`, value: displayType});
  }

  if (def.exampleValue) {
    details.push({icon: `info`, name: `Example`, value: def.exampleValue});
  }

  if (def.count) {
    const name = def.resourceType === ResourceType.User ? `users with property` : `events with property`;
    metrics.push({name, value: abbreviateNumber(def.count, 1, true)});
  }

  if (def.uiQueryCount || def.apiQueryCount) {
    const name = `queries (30 days)`;
    const count = +def.uiQueryCount + +def.apiQueryCount;
    metrics.push({name, value: abbreviateNumber(count, 1, true)});
  }

  const actions = [];
  if (allowEdit && def.customPropertyId) {
    let customPropertyType = `Custom Property`;

    if (isBehavioralCustomProperty(def.customProperty)) {
      customPropertyType = `Behavior`;
    }

    // TODO - scobb - initiate superadmin flow?
    // eslint-disable-next-line camelcase
    const hasPerms =
      !isPersistedCustomProperty(def.customPropertyId) ||
      def.customProperty.canUpdateBasic ||
      isAllowedSuperadminOverride(def);
    const disabled = mpProperty.actionDisabled || !hasPerms;
    const extraActionParams = {
      disabled,
      disabledTooltipText: hasPerms ? `` : `You have view-only access and cannot edit`,
    };
    if (allowDescriptionEdit && hasPerms) {
      editItem = {id: Number(def.customPropertyId), type: EntityTypeNameSingular.CustomProperty, name: def.name};
    }

    actions.push({
      name: `editCustomProperty`,
      label: `Edit ${customPropertyType}`,
      item: {
        property: {
          customPropertyId: def.customPropertyId,
          customProperty: def.customProperty,
        },
      },
      ...extraActionParams,
    });
    if (shareEnabled()) {
      actions.push({
        name: `shareCustomProperty`,
        label: `Share`,
        // Only superadmin or editor can share
        disabled: !getShareCapability(def.customProperty).enableShare,
        disabledTooltipText: `You have view-only access and cannot share`,
        item: {
          property: {
            customPropertyId: def.customPropertyId,
            customProperty: def.customProperty,
          },
          superadminOverrideShare: getShareCapability(def.customProperty).requireSuperadminOverride,
        },
        ...(mpProperty.actionDisabled ? {disabled: true} : {}),
      });
    }
  }

  const headers = getHeaders(def);
  if (def.customPropertyId && def.createdBy) {
    headers.subheader = `Created by ` + def.createdBy.name;
    if (def.lastEdited) {
      headers.subheader += ` - ` + moment(new Date(def.lastEdited)).format(`ll`);
    }
  }

  let lookupTableId;
  if (def[`dimension_data_group_id`]) {
    lookupTableId = def[`dimension_data_group_id`];
  }

  if (def.customProperty && def.customProperty.mappedDataGroupId) {
    lookupTableId = def.customProperty.mappedDataGroupId;
  }

  let lookupTableDetails = {};
  if (lookupTableId) {
    lookupTableDetails = {
      lookupTableName: lookupTables?.[lookupTableId]?.name,
      lookupTableIsTempMapping: Boolean(mpProperty.tempDataGroupId),
    };
  }

  return {
    ...headers,
    editItem,
    description: def.description,
    headerLink,
    details,
    metrics,
    actions,
    ...lookupTableDetails,
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getTooltipDataForCohort(cohort: CohortType, {allowDescriptionEdit = false} = {}): ContextPanelData {
  const isInlineCohort = !!(
    cohort.value &&
    typeof cohort.value !== `number` &&
    cohort.value.cohort &&
    cohort.value.cohort.raw_cohort
  );
  if (!cohort.name && !isInlineCohort) {
    return null;
  }

  let headerLink = null;
  if (!isInlineCohort) {
    headerLink = getCohortLink(cohort);
  }
  const details = [];
  const metrics = [];
  let subheader = getSubHeader(cohort, isInlineCohort);

  if (cohort.lastEdited) {
    subheader += ` - ` + moment(new Date(cohort.lastEdited)).format(`ll`);
  }

  if (Number.isInteger(cohort.count)) {
    const descriptor = cohort.dataGroupId ? `uniques` : `users`;
    metrics.push({name: `${descriptor} in cohort`, value: cohort.count.toLocaleString()});
  }

  let editItem = null;

  if (!isInlineCohort && cohort.can_update_basic && allowDescriptionEdit) {
    editItem = {id: cohort.value, type: `cohort`};
  }

  const hasEditPerms = cohort.canUpdateBasic || isAllowedSuperadminOverride(cohort);

  let actions = null;
  // inline cohort is not considered as a saved entity. A user should always be able to
  // edit an inline cohort but not be able to share.
  const disableEdit = !isInlineCohort && !hasEditPerms;
  if (cohort.value) {
    actions = [
      {
        name: `editCohort`,
        label: `Edit Cohort`,
        icon: `share`,
        disabled: disableEdit,
        disabledTooltipText: disableEdit ? `You have view-only access and cannot edit` : ``,
        item: isInlineCohort
          ? cohort
          : {
              id: cohort.value,
              dataGroupId: cohort.dataGroupId,
            },
      },
    ];

    // inline cohort is not considered as a saved entity thus not shareable
    if (shareEnabled() && !isInlineCohort) {
      actions.push({
        name: `shareCohort`,
        label: `Share`,
        // Only superadmin or editor can share
        disabled: !(cohort.isSuperadmin || cohort.canShare),
        disabledTooltipText: `You have view-only access and cannot share`,
        item: isInlineCohort
          ? {
              ...cohort,
              superadminOverrideShare: getShareCapability(cohort).requireSuperadminOverride,
            }
          : {
              id: cohort.value,
              dataGroupId: cohort.dataGroupId,
              superadminOverrideShare: getShareCapability(cohort).requireSuperadminOverride,
            },
      });
    }
  }

  return {
    header: cohort.name,
    editItem,
    headerLink,
    subheader,
    isUnsaved: isInlineCohort,
    description: cohort.description,
    details,
    metrics,
    actions,
  };
}
